<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="xl"
            ok-only
            ok-title="Close"
            @close="hidden"
            @hidden="hidden"
        >
            <template #modal-title>
                Manage Vocabulary
            </template>

            <div v-if="loading" class="d-flex my-2">
                <b-spinner label="Loading..." class="m-auto" />
            </div>
            <div v-else-if="assignments.length === 0" class="alert alert-warning">
                No vocabulary has been assigned in this publication.
            </div>
            <template v-else>
                <div class="text-right mb-3">
                    <b-dropdown
                        variant="secondary"
                        class="mr-2"
                        right
                    >
                        <template #button-content>
                            <font-awesome-icon :icon="['fad', 'file-alt']" fixed-width />
                            Download Text
                        </template>
                        <b-dropdown-item @click="downloadText('full')">
                            Download Full List
                        </b-dropdown-item>
                        <b-dropdown-item @click="downloadText('glossary')">
                            Download Glossary
                        </b-dropdown-item>
                        <b-dropdown-item @click="downloadText('index')">
                            Download Index
                        </b-dropdown-item>
                    </b-dropdown>
                    <b-button
                        variant="outline-primary"
                        :disabled="downloading_pdf"
                        @click="downloadPdf"
                    >
                        <font-awesome-icon :icon="['fad', 'file-pdf']" fixed-width />
                        Download PDF
                    </b-button>
                </div>
                <b-table
                class="vocabulary-list"
                :items="sorted_assignments"
                :fields="fields"
                :sort-by.sync="sort_by"
                :sort-desc.sync="sort_desc"
                striped
                responsive
                sticky-header
                outlined
                no-border-collapse
                head-variant="light"
                tbody-tr-class="vocabulary-row"
            >
                <template #head(week_num)>
                    Week
                </template>
                <template #head(image)>
                    Image
                </template>
                <template #head(article_title)>
                    Article
                </template>
                <template #head(article_page)>
                    Page
                </template>
                <template #head(actions)>
                    Actions
                </template>

                <template #cell(term)="data">
                    <div class="d-flex align-items-center justify-content-between w-100">
                        <div class="pr-2">{{ data.item.term }}</div>
                        <b-badge
                            v-if="data.item.article_count > 1"
                            variant="danger"
                            class="duplicate-count-badge"
                            :title="formatAlsoAppearsIn(data.item.also_appears_in)"
                            v-b-tooltip.hover
                        >
                            {{ data.item.article_count }}
                        </b-badge>
                    </div>
                </template>
                <template #cell(definition)="data">
                    <div class="definition-cell">
                        {{ data.item.definition }}
                    </div>
                </template>
                <template #cell(image)="data">
                    <div v-if="data.item.image && data.item.image.src" class="vocabulary-image-cell">
                        <img
                            :src="data.item.image.src"
                            :alt="data.item.image.alt || data.item.term || 'Vocabulary image'"
                            class="vocabulary-image-thumbnail"
                        >
                    </div>
                </template>
                <template #cell(actions)="data">
                    <div class="d-flex justify-content-end">
                        <b-button
                            size="sm"
                            variant="primary"
                            class="mr-2"
                            :disabled="!can_edit_assignment || loading_definition_id === data.item.definition_id"
                            @click.stop="openVocabularyTermEditor(data.item)"
                        >
                            Edit
                        </b-button>
                        <b-button
                            size="sm"
                            variant="danger"
                            :disabled="!can_edit_assignment || removing_definition_id === data.item.definition_id"
                            @click.stop="removeAssignment(data.item)"
                        >
                            Delete
                        </b-button>
                    </div>
                </template>
                </b-table>
            </template>
        </b-modal>

        <vocabulary-term-modal
            v-model="show_vocabulary_term_modal"
            :article="selected_article"
            :vocabulary_term="selected_vocabulary_term"
            :vocabulary_terms="selected_article_vocabulary_terms"
            @update="updateVocabularyTerm"
            @hide="hideVocabularyTermModal"
        />
    </div>
</template>

<style lang="scss" scoped>
.vocabulary-list {
    max-height: 60vh;
}

.definition-cell {
    min-height: 24px;
}

.duplicate-count-badge {
    min-width: 24px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    line-height: 1;
    font-weight: 600;
}

.vocabulary-image-cell {
    display: flex;
    align-items: center;
    min-height: 48px;
}

.vocabulary-image-thumbnail {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #dee2e6;
    background: #fff;
}

</style>

<script>
import { mapState } from 'vuex'
import { makeSafeFilename } from '@/common/filename'
import { downloadBlob } from '@/common/fileDownload'
import VocabularyTermModal from '@/components/modals/article/VocabularyTermModal'

export default {
    components: {
        VocabularyTermModal,
    },
    props: [
        'value',
        'publication',
    ],
    data: () => ({
        loading: true,
        assignments: [],
        removing_definition_id: null,
        loading_definition_id: null,
        downloading_pdf: false,
        show_vocabulary_term_modal: false,
        selected_article: null,
        selected_vocabulary_term: null,
        selected_article_vocabulary_terms: [],
        sort_by: 'term',
        sort_desc: false,
        fields: [
            {
                key: 'term',
                sortable: true,
            },
            {
                key: 'definition',
                sortable: false,
            },
            {
                key: 'image',
                sortable: false,
            },
            {
                key: 'week_num',
                sortable: true,
            },
            {
                key: 'article_title',
                sortable: false,
            },
            {
                key: 'article_page',
                sortable: false,
            },
            {
                key: 'actions',
                sortable: false,
                class: 'text-right',
            },
        ],
    }),
    computed: {
        ...mapState({
            user: state => state.user.user,
        }),
        modal_id() {
            return 'publication-vocabulary-modal'
        },
        can_update_article() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_article')
        },
        can_edit_assignment() {
            return this.can_update_article && !this.publication.locked
        },
        sorted_assignments() {
            const assignments = [...this.assignments]

            if (!this.sort_by) {
                return assignments
            }

            if (this.sort_by === 'term') {
                return _.orderBy(
                    assignments,
                    [
                        (assignment) => (assignment.term || '').toLowerCase(),
                        'week_num',
                        'article_page',
                    ],
                    [
                        this.sort_desc ? 'desc' : 'asc',
                        'asc',
                        'asc',
                    ]
                )
            }

            if (this.sort_by === 'week_num') {
                return _.orderBy(
                    assignments,
                    [
                        'week_num',
                        (assignment) => (assignment.term || '').toLowerCase(),
                    ],
                    [
                        this.sort_desc ? 'desc' : 'asc',
                        'asc',
                    ]
                )
            }

            return assignments
        },
    },
    methods: {
        formatAlsoAppearsIn(alsoAppearsIn) {
            if (!alsoAppearsIn || !alsoAppearsIn.length) {
                return ''
            }

            return alsoAppearsIn.map((item) => item.label).join('; ')
        },
        async getVocabulary() {
            this.loading = true
            try {
                const response = await this.axios.get(`/api/publications/${this.publication.id}/vocabulary`)
                this.assignments = response.data
            } catch (e) {
                await this.$swal({
                    html: 'There was a problem loading publication vocabulary.',
                    allowOutsideClick: false,
                })
            } finally {
                this.loading = false
            }
        },
        async openVocabularyTermEditor(assignment) {
            if (!this.can_edit_assignment) {
                return
            }

            this.$store.commit('setValidating', false)
            this.loading_definition_id = assignment.definition_id
            try {
                const response = await this.axios.get(`/api/articles/vocabulary/${assignment.article_id}`)

                this.selected_article = {
                    article_id: assignment.article_id,
                }
                this.selected_vocabulary_term = this.buildSelectedVocabularyTerm(assignment)
                this.selected_article_vocabulary_terms = response.data || []
                this.show_vocabulary_term_modal = true
            } catch (e) {
                await this.$swal({
                    html: 'There was a problem loading article vocabulary for editing.',
                    allowOutsideClick: false,
                })
            } finally {
                this.loading_definition_id = null
            }
        },
        buildSelectedVocabularyTerm(assignment) {
            return {
                vocabulary_term_id: assignment.vocabulary_id,
                definition_id: assignment.definition_id,
                term: assignment.term,
                definition: assignment.definition,
                image: assignment.image,
                sentence: assignment.sentence,
            }
        },
        hideVocabularyTermModal() {
            this.$store.commit('setValidating', false)
            this.show_vocabulary_term_modal = false
            this.selected_article = null
            this.selected_vocabulary_term = null
            this.selected_article_vocabulary_terms = []
        },
        async updateVocabularyTerm(vocabularyTerm, existingVocabularyTerm) {
            if (!this.selected_article || !this.selected_article.article_id) {
                return
            }

            const vocabularyTermId = existingVocabularyTerm && existingVocabularyTerm.id
                ? existingVocabularyTerm.id
                : vocabularyTerm.vocabulary_term_id

            try {
                await this.axios.post(`/api/articles/update_vocabulary/${this.selected_article.article_id}`, {
                    vocabulary_term_id: vocabularyTermId,
                    vocabulary_term: vocabularyTerm.term,
                    definition: vocabularyTerm.definition,
                    image: vocabularyTerm.image,
                    sentence: vocabularyTerm.sentence,
                })

                await this.getVocabulary()
                this.hideVocabularyTermModal()
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to update article vocabulary: ${e.message}`,
                    },
                })
            }
        },
        async removeAssignment(assignment) {
            this.removing_definition_id = assignment.definition_id
            try {
                const termInUse = await this.$store.dispatch('termInCrossword', {
                    unitId: assignment.unit_id,
                    termId: assignment.vocabulary_id,
                })

                let message = `Are you sure you want to remove the vocabulary term <strong>${assignment.term}</strong> from <strong>${assignment.article_title}</strong>?`
                if (termInUse) {
                    message += '<p class="mt-3 mb-0"><strong>Warning:</strong> This term is used by the crossword for this week. Removing it will reset the crossword.</p>'
                }

                const result = await this.$swal({
                    html: message,
                    showCancelButton: true,
                    confirmButtonText: 'Remove Vocabulary Term',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                })
                if (!result.value) {
                    return
                }

                const response = await this.axios.post(
                    `/api/publications/${this.publication.id}/vocabulary/${assignment.definition_id}/remove`
                )
                if (response.data && termInUse) {
                    await this.$store.dispatch('saveCrossword', {
                        json: null,
                        unit_id: assignment.unit_id,
                    })
                }

                _.remove(this.assignments, {definition_id: assignment.definition_id})
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to remove publication vocabulary assignment: ${e.message}`,
                    },
                })
            } finally {
                this.removing_definition_id = null
            }
            this.refreshDuplicateNotes()
        },
        refreshDuplicateNotes() {
            const groupedAssignments = _.groupBy(
                this.assignments,
                (assignment) => (assignment.term || '').toLowerCase()
            )
            this.assignments = this.assignments.map((assignment) => {
                const alsoAppearsIn = (groupedAssignments[(assignment.term || '').toLowerCase()] || [])
                    .filter((relatedAssignment) => relatedAssignment.article_id !== assignment.article_id)
                    .map((relatedAssignment) => ({
                        article_id: relatedAssignment.article_id,
                        article_title: relatedAssignment.article_title,
                        week_num: relatedAssignment.week_num,
                        article_page: relatedAssignment.article_page,
                        label: `Week ${relatedAssignment.week_num != null ? relatedAssignment.week_num : '?'}, Page ${relatedAssignment.article_page != null ? relatedAssignment.article_page : '?'}, ${relatedAssignment.article_title || 'Untitled Article'}`,
                    }))
                    .filter((relatedAssignment, index, relatedAssignments) => {
                        return relatedAssignments.findIndex((candidate) => candidate.label === relatedAssignment.label) === index
                    })

                return {
                    ...assignment,
                    article_count: alsoAppearsIn.length + 1,
                    also_appears_in: alsoAppearsIn,
                }
            })
        },
        hidden() {
            this.hideVocabularyTermModal()
            this.$emit('hide')
        },
        buildExportHeader(title, options = {}) {
            const lines = []

            lines.push(title)
            if (this.publication.title) {
                lines.push(this.publication.title)
            }
            if (this.publication.sku) {
                lines.push(`SKU: ${this.publication.sku}`)
            }

            if (options.includeSortLabel !== false) {
                const sortLabel = this.sort_by === 'term'
                    ? 'Term'
                    : this.sort_by === 'week_num'
                        ? 'Week'
                        : 'Default'
                const sortDirection = this.sort_by
                    ? ` (${this.sort_desc ? 'descending' : 'ascending'})`
                    : ''
                lines.push(`Sorted by: ${sortLabel}${sortDirection}`)
            }

            lines.push('')

            return lines
        },
        buildGlossaryEntries() {
            return _.chain(this.sorted_assignments)
                .groupBy((assignment) => (assignment.term || '').trim().toLowerCase())
                .values()
                .map((assignments) => {
                    const primaryAssignment = _.orderBy(
                        assignments,
                        [
                            (assignment) => (assignment.term || '').toLowerCase(),
                            'week_num',
                            'article_page',
                        ],
                        ['asc', 'asc', 'asc']
                    )[0]
                    const references = _.chain(assignments)
                        .map((assignment) => this.formatAssignmentLocation(assignment))
                        .filter(Boolean)
                        .uniq()
                        .value()

                    return {
                        term: primaryAssignment.term || '',
                        definition: primaryAssignment.definition || '',
                        references,
                    }
                })
                .orderBy((entry) => (entry.term || '').toLowerCase(), 'asc')
                .value()
        },
        buildIndexEntries() {
            return _.chain(this.sorted_assignments)
                .groupBy((assignment) => (assignment.term || '').trim().toLowerCase())
                .values()
                .map((assignments) => {
                    const primaryAssignment = _.orderBy(
                        assignments,
                        [
                            (assignment) => (assignment.term || '').toLowerCase(),
                            'week_num',
                            'article_page',
                        ],
                        ['asc', 'asc', 'asc']
                    )[0]
                    const weekNumbers = _.chain(assignments)
                        .map((assignment) => assignment.week_num)
                        .filter((weekNum) => weekNum != null && weekNum !== '')
                        .uniq()
                        .sortBy()
                        .value()

                    return {
                        term: primaryAssignment.term || '',
                        weekNumbers,
                    }
                })
                .orderBy((entry) => (entry.term || '').toLowerCase(), 'asc')
                .value()
        },
        formatAssignmentLocation(assignment) {
            const parts = []

            if (assignment.week_num != null) {
                parts.push(`Week ${assignment.week_num}`)
            }
            if (assignment.article_page != null) {
                parts.push(`Page ${assignment.article_page}`)
            }
            if (assignment.article_title) {
                parts.push(assignment.article_title)
            }

            return parts.join(', ')
        },
        buildFullTextLines() {
            const lines = this.buildExportHeader('Publication Vocabulary')

            this.sorted_assignments.forEach((assignment, index) => {
                const duplicateCountText = assignment.article_count > 1
                    ? ` [${assignment.article_count}]`
                    : ''
                lines.push(`${index + 1}. Term: ${assignment.term || ''}${duplicateCountText}`)
                lines.push(`Definition: ${assignment.definition || ''}`)
                lines.push(`Week: ${assignment.week_num != null ? assignment.week_num : ''}`)
                lines.push(`Article: ${assignment.article_title || ''}`)

                if (assignment.also_appears_in && assignment.also_appears_in.length) {
                    lines.push(`Also appears in: ${this.formatAlsoAppearsIn(assignment.also_appears_in)}`)
                }

                lines.push('')
            })

            return lines
        },
        buildGlossaryLines() {
            const lines = this.buildExportHeader('Publication Vocabulary Glossary', {
                includeSortLabel: false,
            })

            this.buildGlossaryEntries().forEach((entry) => {
                lines.push(`${entry.term}: ${entry.definition}`)
            })

            return lines
        },
        buildIndexLines() {
            const lines = this.buildExportHeader('Publication Vocabulary Index', {
                includeSortLabel: false,
            })
            let currentLetter = null

            this.buildIndexEntries().forEach((entry) => {
                const firstLetter = (entry.term || '').trim().charAt(0).toUpperCase() || '#'

                if (firstLetter !== currentLetter) {
                    if (currentLetter !== null) {
                        lines.push('')
                    }

                    currentLetter = firstLetter
                    lines.push(currentLetter)
                }

                lines.push(`${entry.term}: ${entry.weekNumbers.join(', ')}`)
            })

            return lines
        },
        getTextDownloadConfig(format = 'full') {
            if (format === 'glossary') {
                return {
                    lines: this.buildGlossaryLines(),
                    suffix: 'glossary',
                }
            }

            if (format === 'index') {
                return {
                    lines: this.buildIndexLines(),
                    suffix: 'index',
                }
            }

            return {
                lines: this.buildFullTextLines(),
                suffix: 'vocabulary',
            }
        },
        downloadText(format = 'full') {
            const { lines, suffix } = this.getTextDownloadConfig(format)
            const text = lines.join('\n')
            const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
            const fileName = makeSafeFilename(this.publication.sku || this.publication.title || 'publication-vocabulary')

            downloadBlob(blob, `${fileName} ${suffix}.txt`)
        },
        async downloadPdf() {
            this.downloading_pdf = true

            try {
                const response = await this.axios.post(
                    `/api/publications/${this.publication.id}/vocabulary/pdf`,
                    {
                        definition_ids: this.sorted_assignments.map((assignment) => assignment.definition_id),
                        sort_by: this.sort_by,
                        sort_desc: this.sort_desc,
                    },
                    {
                        responseType: 'blob',
                    }
                )
                const fileName = makeSafeFilename(this.publication.sku || this.publication.title || 'publication-vocabulary')

                downloadBlob(response.data, `${fileName} vocabulary.pdf`)
            } catch (e) {
                await this.$swal({
                    html: 'There was a problem generating the vocabulary PDF.',
                    allowOutsideClick: false,
                })
            } finally {
                this.downloading_pdf = false
            }
        },
    },
    async mounted() {
        await this.getVocabulary()
    },
}
</script>
